import { isEqual } from 'lodash';
import { type CSSProperties, memo, useCallback, useMemo } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';
import { type Upload } from '@air/redux-uploader';

import { MobileUploadTableRow } from '~/components/TableView/MobileTableRow/MobileUploadTableRow';
import { UploadNameCell } from '~/components/TableView/NameCell/UploadNameCell';
import { TableCell } from '~/components/TableView/TableCell';
import { ConfigurableTableCells } from '~/components/TableView/TableRow/ConfigurableTableCells';
import { DesktopTableRowContainer } from '~/components/TableView/TableRow/DesktopTableRowContainer';
import { TableRow } from '~/components/TableView/TableRow/TableRow';
import { useTableView } from '~/components/TableView/TableViewContext';
import { uploadTableRowInfo } from '~/components/TableView/utils';
import { type ConfigViewOption } from '~/store/configViews/types';
import { makeActiveTableColumnFromOption } from '../tableColumnWidthsUtils';

export type UploadTableRowProps = {
  upload: Upload;
  /**
   * `react-virtualized` warns that `style` property should be included in the rendered cell for positioning
   * so we've separated it from the `tx` prop
   * */
  style: CSSProperties;
};

export const UploadTableRow = memo(({ upload, style }: UploadTableRowProps) => {
  const uploadRowInfo = useMemo(() => uploadTableRowInfo(upload), [upload]);
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { getColumnWidthStyle } = useTableView();

  const renderCustomFieldTableCellComponent = useCallback(
    (option: ConfigViewOption) => (
      <TableCell
        style={getColumnWidthStyle(
          makeActiveTableColumnFromOption(option.fieldName, option.isCustomField, option.customFieldId),
        )}
      />
    ),
    [getColumnWidthStyle],
  );

  return (
    <>
      {isAboveMediumScreen ? (
        <DesktopTableRowContainer style={style}>
          <TableRow>
            <UploadNameCell upload={upload} />
            <ConfigurableTableCells
              renderCustomFieldTableCellComponent={renderCustomFieldTableCellComponent}
              rowInfo={uploadRowInfo}
            />
          </TableRow>
        </DesktopTableRowContainer>
      ) : (
        <MobileUploadTableRow upload={upload} style={style} />
      )}
    </>
  );
}, isEqual);

UploadTableRow.displayName = 'UploadTableRow';
